import { z } from 'zod';
import { CURRENCY_CODES } from './money.js';

/** Phase 3: customers & credit, supplier balances, expenses, tills, shifts and cash. */

const nullableText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const id = z.uuid();
const amount = z.number().int().positive('Enter an amount').max(1_000_000_000_00);
const currency = z.enum(CURRENCY_CODES as [string, ...string[]]);
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-10-21');

export const NON_ACCOUNT_METHODS = ['cash', 'card', 'ecocash', 'onemoney', 'innbucks', 'omari', 'bank_transfer'] as const;

export const customerSchema = z.object({
  name: z.string().trim().min(2, 'Enter the customer name').max(120),
  phone: z
    .string()
    .trim()
    .max(20)
    .regex(/^\+?[0-9 ()-]*$/, 'Use digits only')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  email: z
    .string()
    .trim()
    .max(120)
    .transform((v) => (v === '' ? null : v))
    .pipe(z.email('Enter a valid email').nullable())
    .nullable()
    .optional(),
  idNumber: nullableText(30),
  /** Business customers: ZIMRA taxpayer number (10 digits) and VAT number, for their fiscal invoices. */
  tin: z
    .string()
    .trim()
    .regex(/^(\d{10})?$/, 'A TIN has 10 digits')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  vatNumber: nullableText(20),
  address: nullableText(200),
  /** 0 = no credit allowed. */
  creditLimitCents: z.number().int().min(0).max(1_000_000_000_00).default(0),
  paymentTermsDays: z.number().int().min(0).max(365).default(30),
  notes: nullableText(500),
  isActive: z.boolean().default(true),
});
export type CustomerInput = z.infer<typeof customerSchema>;

export const accountPaymentSchema = z.object({
  branchId: id,
  amountCents: amount,
  method: z.enum(NON_ACCOUNT_METHODS),
  currency: currency.nullable().optional(),
  reference: nullableText(60),
  note: nullableText(200),
});
export type AccountPaymentInput = z.infer<typeof accountPaymentSchema>;

export const balanceAdjustmentSchema = z.object({
  kind: z.enum(['opening_balance', 'adjustment', 'credit_note']),
  /** Positive increases the balance owed, negative reduces it. */
  amountCents: z
    .number()
    .int()
    .refine((v) => v !== 0, 'Amount cannot be zero'),
  note: z.string().trim().min(3, 'Explain the adjustment').max(200),
  reference: nullableText(60),
});

export const supplierPaymentSchema = z.object({
  branchId: id,
  amountCents: amount,
  method: z.enum(NON_ACCOUNT_METHODS),
  reference: nullableText(60),
  note: nullableText(200),
  /** Cash taken out of the payer's open till shift. */
  fromTill: z.boolean().default(false),
});

export const expenseCategorySchema = z.object({ name: z.string().trim().min(2).max(60) });

export const expenseSchema = z.object({
  branchId: id,
  categoryId: id,
  expenseDate: dateOnly,
  description: z.string().trim().min(3, 'Describe the expense').max(200),
  amountCents: amount,
  method: z.enum(NON_ACCOUNT_METHODS),
  reference: nullableText(60),
  supplierId: id.nullable().optional(),
  /** Cash paid out of the recorder's open till shift. */
  fromTill: z.boolean().default(false),
});
export type ExpenseInput = z.infer<typeof expenseSchema>;

export const expenseDecisionSchema = z.object({
  approve: z.boolean(),
  reason: nullableText(200),
});

export const tillSchema = z.object({
  branchId: id,
  name: z.string().trim().min(2).max(40),
  isActive: z.boolean().default(true),
});

export const openShiftSchema = z.object({
  tillId: id,
  floats: z
    .array(z.object({ currency, amountCents: z.number().int().min(0).max(1_000_000_00) }))
    .max(6)
    .default([]),
});

export const cashMovementSchema = z.object({
  kind: z.enum(['pay_in', 'pay_out', 'drop']),
  currency,
  amountCents: amount,
  reason: z.string().trim().min(3, 'Say what the cash was for').max(200),
});

export const closeShiftSchema = z.object({
  counts: z.array(z.object({ currency, countedCents: z.number().int().min(0).max(1_000_000_000_00) })).max(6),
  note: nullableText(300),
});

export const reconcileShiftSchema = z.object({ note: nullableText(300) });

export const CASH_MOVEMENT_KINDS = { pay_in: 'Cash added', pay_out: 'Cash paid out', drop: 'Moved to safe' } as const;

export const DEFAULT_EXPENSE_CATEGORIES = [
  'Rent',
  'Electricity (ZESA)',
  'Generator fuel',
  'Water and rates',
  'Salaries and wages',
  'Transport and fuel',
  'Repairs and maintenance',
  'Security',
  'Phone and internet',
  'Bank charges and IMTT',
  'Licences and levies',
  'Cleaning and supplies',
  'Other',
];

/** Ageing buckets by days past due. */
export const AGEING_BUCKETS = [
  { key: 'current', label: 'Not yet due', min: -Infinity, max: 0 },
  { key: 'd1_30', label: '1–30 days overdue', min: 1, max: 30 },
  { key: 'd31_60', label: '31–60 days', min: 31, max: 60 },
  { key: 'd61_90', label: '61–90 days', min: 61, max: 90 },
  { key: 'd90p', label: 'Over 90 days', min: 91, max: Infinity },
] as const;
export type AgeingKey = (typeof AGEING_BUCKETS)[number]['key'];

export interface LedgerEntryLike {
  amountCents: number;
  dueDate: string | null;
  createdAt: Date | string;
}

/**
 * Ages a running account: credits (payments, returns) settle the oldest debits first;
 * what remains is bucketed by how many days past its due date it is on `asOf`.
 */
export function ageBalance(entries: LedgerEntryLike[], asOf: string): Record<AgeingKey, number> & { total: number; overdue: number } {
  const debits = entries
    .filter((e) => e.amountCents > 0)
    .map((e) => ({ open: e.amountCents, due: e.dueDate ?? toDate(e.createdAt) }))
    .sort((a, b) => (a.due < b.due ? -1 : a.due > b.due ? 1 : 0));
  let credit = -entries.filter((e) => e.amountCents < 0).reduce((a, e) => a + e.amountCents, 0);
  for (const d of debits) {
    const used = Math.min(d.open, credit);
    d.open -= used;
    credit -= used;
  }
  const out = { current: 0, d1_30: 0, d31_60: 0, d61_90: 0, d90p: 0, total: 0, overdue: 0 } as Record<AgeingKey, number> & { total: number; overdue: number };
  for (const d of debits) {
    if (!d.open) continue;
    const days = daysBetween(d.due, asOf);
    const b = AGEING_BUCKETS.find((x) => days >= x.min && days <= x.max)!;
    out[b.key] += d.open;
    out.total += d.open;
    if (days > 0) out.overdue += d.open;
  }
  // Unapplied credit (customer paid in advance) shows as a negative current balance.
  if (credit > 0) {
    out.current -= credit;
    out.total -= credit;
  }
  return out;
}

function toDate(d: Date | string): string {
  return typeof d === 'string' ? d.slice(0, 10) : new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Harare' }).format(d);
}
function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}
